import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import ArrowBack from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import TableViewIcon from "@mui/icons-material/TableView";
import GridOnIcon from "@mui/icons-material/GridOn";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";

import {
  getUserStats,
  getUserReportOptions,
  previewUserReport,
  downloadUserReportXlsx,
  saveUserReport,
  deleteUserReport,
} from "../../services/backend/UserReportService";
import { PERMISSIONS } from "../../constants";
import { StatCard, ChartCard } from "../generic/ReportCards";
import { INSTITUTIONAL_ROLE_COLUMNS, ROLE_GROUPS, ROLE_LABELS } from "./roleLabels";
import UserReportExclusions, {
  EMPTY_EXCLUSIONS,
  exclusionCount,
  exclusionsForRequest,
} from "./UserReportExclusions";
import SavedUserReports from "./SavedUserReports";

// Same palettes as the PQRSD Informes page.
const SERIES = ["#2a78d6", "#1baf7a", "#eda100", "#4a3aa7", "#e34948", "#eb6834"];
// Active and inactive keep one colour on every chart and tile of the page; a
// disabling is what makes a user inactive, so it shares the inactive colour.
const ACTIVE = SERIES[0];
const INACTIVE = SERIES[4];
// Changes made by an ADMIN account ("de soporte").
const SUPPORT = SERIES[5];

// A long tail of dependencias would make the chart unreadable; the report
// builder lists all of them.
const TOP_DEPENDENCIES = 15;

const fmtInt = (n) => new Intl.NumberFormat("es-CO").format(n || 0);
const pctOf = (n, total) => (total > 0 ? `${((n / total) * 100).toFixed(1)}%` : "—");

// Preset ranges. Values are moment objects (same convention as PqrsdReports).
const PRESETS = [
  { key: "month", label: "Este mes", from: () => moment().startOf("month"), to: () => moment().endOf("day") },
  {
    key: "lastMonth",
    label: "Mes anterior",
    from: () => moment().subtract(1, "month").startOf("month"),
    to: () => moment().subtract(1, "month").endOf("month"),
  },
  { key: "3m", label: "Últimos 3 meses", from: () => moment().subtract(2, "months").startOf("month"), to: () => moment().endOf("day") },
  { key: "12m", label: "Últimos 12 meses", from: () => moment().subtract(11, "months").startOf("month"), to: () => moment().endOf("day") },
  { key: "all", label: "Todo", from: () => null, to: () => null },
];

// The range as the backend wants it. The backend counts in Bogotá months, so
// the picked days are Bogotá days too, whatever time zone the browser is in (a
// computer set to another zone would otherwise shift every bound by hours).
const BOGOTA_OFFSET = "-05:00";
const rangeOf = (f, t) => ({
  from: f ? `${f.format("YYYY-MM-DD")}T00:00:00.000${BOGOTA_OFFSET}` : null,
  to: t ? `${t.format("YYYY-MM-DD")}T23:59:59.999${BOGOTA_OFFSET}` : null,
});

// Why a picked range can't be asked for, or null when it can. A half-typed
// date is an invalid moment; an empty field is null (no bound).
const rangeProblem = (f, t) => {
  if ((f && !f.isValid()) || (t && !t.isValid())) return "Fecha incompleta o inválida.";
  if (f && t && f.isAfter(t, "day")) return "«Desde» es posterior a «Hasta».";
  return null;
};

// "01/09/2026 – 29/09/2026", "desde 01/09/2026", "hasta …" or the whole history.
const describeRange = (f, t) => {
  const fmt = (m) => m.format("DD/MM/YYYY");
  if (f && t) return `${fmt(f)} – ${fmt(t)}`;
  if (f) return `desde ${fmt(f)}`;
  if (t) return `hasta ${fmt(t)}`;
  return "todo el historial";
};

const SAVED_ERRORS = {
  NOT_FOUND: "Ese informe guardado ya no existe: alguien lo eliminó. Guárdelo como uno nuevo.",
};

const ERROR_TEXT = {
  FORBIDDEN: "Su cuenta no tiene permiso para ver los informes de usuarios (user_admin.manage).",
  FAILED: "No fue posible cargar las estadísticas.",
};

// An instant from the backend, shown as its Bogotá date.
const inBogota = (iso) => (iso ? moment(iso).utcOffset(BOGOTA_OFFSET) : null);

// Every role, listed by module in the order of the report's columns.
const ROLE_OPTIONS = ROLE_GROUPS.flatMap((g) =>
  g.roles.map(([key]) => ({ key, label: ROLE_LABELS[key] || key, group: g.label }))
);
const INSTITUTIONAL_OPTIONS = ROLE_OPTIONS.filter((o) => INSTITUTIONAL_ROLE_COLUMNS.includes(o.key));

const STATUS_OPTIONS = [
  { key: "active", label: "Activos" },
  { key: "inactive", label: "Inactivos" },
  { key: "all", label: "Todos" },
];

// Dependencias run to 50+ characters; past this they are cut with an ellipsis
// on the axis and its tooltip (x-charts 6 formats both alike). The report
// builder keeps the full name.
const MAX_BAR_LABEL = 38;
const shortLabel = (l) => {
  const s = String(l);
  return s.length > MAX_BAR_LABEL ? `${s.slice(0, MAX_BAR_LABEL - 1).trimEnd()}…` : s;
};

// Left margin that fits the longest (shortened) band label. Upper-case text,
// which most dependencias are, runs up to ~8px per character at the axis' 12px.
const barLabelMargin = (labels) =>
  Math.max(80, Math.ceil(Math.max(0, ...labels.map((l) => shortLabel(l).length)) * 8) + 16);

// RFC 4180 CSV with a BOM so Excel reads the accents, like exportContractsToCSV.
const downloadCsv = (fileName, columns, rows) => {
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  // One header row, so a role column carries its module: "CONTRATOS - SUPERVISOR".
  const csv = [columns.map((c) => escape(c.fullLabel || c.label)), ...rows.map((r) => r.map(escape))]
    .map((line) => line.join(","))
    .join("\r\n");
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoked a moment later: revoking right after click() can cancel the
  // download before the browser has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// A paginated read-only table of { columns, rows }, the exact cells the file
// will hold. Role columns (X marks) are narrow and centred.
// `markMissing`: an empty cell reads "Sin dato" in red, as in the Excel
// (role cells excepted: empty there means the role isn't held). Used on the
// users list and the findings list.
function PreviewTable({ columns, rows, markMissing = false }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  useEffect(() => setPage(0), [rows]);

  // The role block gets the file's grouped header: "PERMISOS Y ROLES ACTIVOS",
  // then the module over its roles.
  const firstRole = columns.findIndex((c) => c.role);
  const hasRoles = firstRole !== -1;
  const roleCount = columns.filter((c) => c.role).length;
  const spans = [];
  columns.forEach((c, i) => {
    if (!c.role) return;
    const last = spans[spans.length - 1];
    if (last && last[0] === c.group && last[2] === i - 1) last[2] = i;
    else spans.push([c.group, i, i]);
  });
  // Sticky header rows stack: each sits one row height below the previous.
  const HEAD_ROW = 33;
  const headSx = (row, bold) => ({
    top: row * HEAD_ROW,
    height: HEAD_ROW,
    boxSizing: "border-box",
    py: 0.5,
    fontWeight: bold ? 600 : 400,
    whiteSpace: "nowrap",
    borderRight: 1,
    borderColor: "divider",
  });

  return (
    <>
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            {hasRoles ? (
              <>
                <TableRow>
                  {columns.map((c, i) =>
                    c.role ? (
                      i === firstRole && (
                        <TableCell key="roles-title" colSpan={roleCount} align="center" sx={headSx(0, true)}>
                          PERMISOS Y ROLES ACTIVOS
                        </TableCell>
                      )
                    ) : (
                      <TableCell key={c.key} rowSpan={3} sx={headSx(0, true)}>
                        {c.label}
                      </TableCell>
                    )
                  )}
                </TableRow>
                <TableRow>
                  {spans.map(([group, a, b]) => (
                    <TableCell key={`${group}-${a}`} colSpan={b - a + 1} align="center" sx={headSx(1, true)}>
                      {group}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {columns.map((c) => c.role && (
                    <TableCell key={c.key} align="center" sx={headSx(2, false)}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </>
            ) : (
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c.key} sx={headSx(0, true)}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, i) => (
              <TableRow key={`${page}-${i}`} hover>
                {row.map((cell, j) => {
                  // A role held fills the cell black, as in the Excel;
                  // EXPEDIENTES shows what gives the access in white.
                  const held = columns[j].role && !!cell;
                  const missing = markMissing && !cell && !columns[j].role;
                  return (
                    <TableCell
                      key={columns[j].key}
                      align={columns[j].role ? "center" : "left"}
                      title={held ? `${columns[j].fullLabel}${cell === "X" ? "" : `: ${cell}`}` : undefined}
                      sx={{
                        whiteSpace: "nowrap",
                        borderRight: 1,
                        borderColor: "divider",
                        ...(held && { bgcolor: "#000", color: "#fff", fontSize: 11, fontWeight: 700 }),
                        ...(missing && { color: "error.main", fontWeight: 700 }),
                      }}
                    >
                      {missing ? "Sin dato" : held && cell === "X" ? "" : cell}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Filas por página"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
    </>
  );
}

// The findings the system sees in its own data, one line per kind with its
// count and what support should do. The report only shows them.
// Each line can be clicked to see that case's users (`onSelect(key)`); the
// selected one is highlighted.
function FindingsSummary({ summary, selected, onSelect }) {
  return (
    <Stack spacing={0.5}>
      {summary.map((f) => {
        const isSelected = selected === f.key;
        return (
          <Stack
            key={f.key}
            direction={{ xs: "column", sm: "row" }}
            spacing={{ sm: 1.5 }}
            alignItems={{ sm: "baseline" }}
            role={onSelect ? "button" : undefined}
            tabIndex={onSelect ? 0 : undefined}
            aria-pressed={onSelect ? isSelected : undefined}
            title={onSelect ? (isSelected ? "Ver todos los hallazgos" : "Ver solo estos casos") : undefined}
            onClick={onSelect ? () => onSelect(f.key) : undefined}
            onKeyDown={onSelect ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(f.key)) : undefined}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              cursor: onSelect ? "pointer" : "default",
              bgcolor: isSelected ? "action.selected" : "transparent",
              "&:hover": onSelect ? { bgcolor: isSelected ? "action.selected" : "action.hover" } : undefined,
            }}
          >
            <Chip size="small" label={fmtInt(f.count)} color="warning" sx={{ fontWeight: 700, minWidth: 48 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: { sm: 360 }, textDecoration: onSelect ? "underline dotted" : "none" }}>
              {f.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {f.action}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}

const FINDINGS_NOTE =
  "Inconsistencias que el sistema encuentra en sus propios datos. El informe solo las muestra: soporte las revisa y las corrige a mano.";

export default function UserReports() {
  const navigate = useNavigate();
  const theme = useTheme();
  const user = useSelector((state) => state.user.value);

  const [tab, setTab] = useState("dashboard");
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState(PRESETS[0].from());
  const [to, setTo] = useState(PRESETS[0].to());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // "FORBIDDEN" | "FAILED"
  const [snack, setSnack] = useState("");
  // Only the latest request may land: a slow answer to an earlier preset must
  // not overwrite the one the user is looking at.
  const requestSeq = useRef(0);

  // Report builder.
  const [status, setStatus] = useState("active");
  const [dependencies, setDependencies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleColumns, setRoleColumns] = useState([]);
  const [roleMatch, setRoleMatch] = useState("any");
  const [withChanges, setWithChanges] = useState(false);
  const [accountOnly, setAccountOnly] = useState(false);
  const [exclusions, setExclusions] = useState(EMPTY_EXCLUSIONS);
  // What the dependencia and exclusion pickers offer, over every user.
  const [options, setOptions] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Saved reports (config/userReports) and the one loaded, if any.
  const [savedReports, setSavedReports] = useState([]);
  const [currentSavedId, setCurrentSavedId] = useState(null);
  const [preview, setPreview] = useState(null);
  // The one finding whose users the list shows (null: all of them).
  const [findingFilter, setFindingFilter] = useState(null);
  const findingsRef = useRef(null);
  // Set when a dashboard finding opens the builder: scroll to the list once
  // the preview carrying it has rendered.
  const scrollToFindings = useRef(false);
  const [busy, setBusy] = useState(null); // "preview" | "xlsx" | "csv"

  const isFullAdmin = user?.roles?.includes("ADMIN");
  const canView = isFullAdmin || user?.permissions?.includes(PERMISSIONS.USER_ADMIN.MANAGE);

  // The filters and exclusions shared by the dashboard and the builder, as the
  // backend takes them. Half-filled exclusion rules are left out, so editing
  // one doesn't reload anything until it would exclude someone.
  const scope = useMemo(
    () => ({
      dependencies: dependencies.map((d) => d.key),
      roles: roles.map((r) => r.key),
      roleMatch,
      accountOnly,
      exclusions: exclusionsForRequest(exclusions),
    }),
    [dependencies, roles, roleMatch, accountOnly, exclusions]
  );
  const scopeKey = JSON.stringify(scope);
  // Read by load(), which every range change and "Actualizar" calls.
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const load = useCallback(async (f, t) => {
    if (rangeProblem(f, t)) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    const { body, error: err } = await getUserStats({ ...rangeOf(f, t), ...scopeRef.current });
    if (seq !== requestSeq.current) return;
    setError(err);
    setStats(body);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!canView) return;
    load(from, to);
    getUserReportOptions().then(({ body }) => {
      if (!body) return;
      setOptions(body);
      setSavedReports(body.savedReports || []);
    });
    // Initial load only; later loads are triggered explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The dashboard follows the filters: reloaded a moment after the last change
  // (each chip is a change), not on the first render, which loads above.
  const scopeLoaded = useRef(scopeKey);
  useEffect(() => {
    if (!canView || scopeLoaded.current === scopeKey) return undefined;
    const timer = setTimeout(() => {
      scopeLoaded.current = scopeKey;
      load(from, to);
    }, 600);
    return () => clearTimeout(timer);
    // from/to changes load on their own (applyPreset, applyCustom).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const clearScope = () => {
    setDependencies([]);
    setRoles([]);
    setRoleMatch("any");
    setAccountOnly(false);
    setExclusions(EMPTY_EXCLUSIONS);
  };

  // What the panel's summary line says is applied.
  const plural = (n, one, many) => `${fmtInt(n)} ${n === 1 ? one : many}`;
  const scopeChips = [
    dependencies.length && plural(dependencies.length, "dependencia", "dependencias"),
    roles.length && plural(roles.length, "rol", "roles"),
    accountOnly && "Solo con cuenta de acceso",
    exclusionCount(exclusions) && `${plural(exclusionCount(exclusions), "exclusión", "exclusiones")}`,
  ].filter(Boolean);

  // --- saved reports ---------------------------------------------------------
  // Everything a saved report holds, as it is sent and stored (no period).
  const settings = {
    ...scope,
    status,
    roleColumns: roleColumns.map((r) => r.key),
    withChanges,
  };
  // A saved report and the settings on screen compare on the same fields.
  const comparable = (r) =>
    JSON.stringify({
      status: r.status,
      dependencies: r.dependencies,
      roles: r.roles,
      roleMatch: r.roleMatch,
      accountOnly: !!r.accountOnly,
      roleColumns: r.roleColumns,
      withChanges: !!r.withChanges,
      exclusions: exclusionsForRequest(r.exclusions || EMPTY_EXCLUSIONS),
    });
  const currentSaved = savedReports.find((r) => r.id === currentSavedId) || null;
  const savedDirty = !!currentSaved && comparable(currentSaved) !== comparable(settings);

  // Keys back into the pickers' options; one no longer offered (a role
  // renamed, a dependencia nobody has now) still shows by its key.
  const roleOption = (key) => ROLE_OPTIONS.find((o) => o.key === key) || { key, label: key, group: "" };
  const applySaved = (r) => {
    setStatus(r.status || "all");
    setDependencies(
      (r.dependencies || []).map(
        (key) => (options?.dependencies || []).find((d) => d.key === key) || { key, label: key, count: 0 }
      )
    );
    setRoles((r.roles || []).map(roleOption));
    setRoleMatch(r.roleMatch === "all" ? "all" : "any");
    setAccountOnly(!!r.accountOnly);
    setRoleColumns((r.roleColumns || []).map(roleOption));
    setWithChanges(!!r.withChanges);
    const ex = r.exclusions || EMPTY_EXCLUSIONS;
    setExclusions({
      rules: (ex.rules || []).map((rule) => ({ ...rule, values: [...(rule.values || [])] })),
      emailDomains: [...(ex.emailDomains || [])],
      users: [...(ex.users || [])],
    });
    setCurrentSavedId(r.id);
    setSnack(`Informe «${r.name}» cargado.`);
  };

  // Returns null, or the error code for the name dialog to show.
  const saveCurrent = async ({ id, name }) => {
    const { body, error: err } = await saveUserReport({ ...settings, id, name });
    if (err) {
      if (!id || err !== "NOT_FOUND") return err;
      // Deleted by someone else meanwhile: drop it here too; the filters on
      // screen stay, ready for "Guardar como nuevo".
      setSavedReports((list) => list.filter((r) => r.id !== id));
      setCurrentSavedId(null);
      setSnack(SAVED_ERRORS.NOT_FOUND);
      return err;
    }
    setSavedReports(body.savedReports);
    setCurrentSavedId(body.saved.id);
    setSnack(`Informe «${body.saved.name}» guardado.`);
    return null;
  };

  const deleteCurrent = async (id) => {
    const { body, error: err } = await deleteUserReport(id);
    if (err && err !== "NOT_FOUND") return setSnack("No fue posible eliminar el informe guardado.");
    if (body) setSavedReports(body.savedReports);
    else setSavedReports((list) => list.filter((r) => r.id !== id));
    setCurrentSavedId(null);
    setSnack("Informe guardado eliminado.");
  };

  // A preview describes the filters it was made with; any change retires it.
  useEffect(
    () => setPreview(null),
    [status, dependencies, roles, roleMatch, roleColumns, accountOnly, exclusions, withChanges, from, to]
  );

  // The finding the list is narrowed to, if it is in this preview; its rows
  // are matched by the finding's label (the table's first column).
  const selectedFinding = preview?.findings?.summary.find((f) => f.key === findingFilter) || null;
  const findingRows = useMemo(() => {
    const rows = preview?.findings?.rows || [];
    return selectedFinding ? rows.filter((r) => r[0] === selectedFinding.label) : rows;
  }, [preview, selectedFinding]);

  const applyPreset = (p) => {
    setPreset(p.key);
    const f = p.from();
    const t = p.to();
    setFrom(f);
    setTo(t);
    load(f, t);
  };

  const applyCustom = (f, t) => {
    setPreset("custom");
    load(f, t);
  };

  const axisSx = useMemo(
    () => ({
      "& .MuiChartsAxis-tickLabel": { fill: theme.palette.text.secondary },
      "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: theme.palette.divider },
    }),
    [theme]
  );

  const invalidRange = rangeProblem(from, to);
  // What the builder will ask for (the pickers)...
  const periodLabel = invalidRange ? "—" : describeRange(from, to);
  // ...and what the figures on screen were computed for (the backend's answer),
  // which differ while a new range is loading or can't be asked for.
  const statsPeriodLabel = describeRange(
    inBogota(stats?.range?.from),
    inBogota(stats?.range?.to)
  );

  // Every exported PNG states the period it was computed for, taken from the
  // range the backend answered (not the pickers, which may have moved since).
  const exportInfo = useMemo(() => {
    const range = stats?.range;
    const f = inBogota(range?.from);
    const t = inBogota(range?.to);
    const subtitle = `Período: ${describeRange(f, t)}`;
    const rangeKey = f || t
      ? `${f ? f.format("YYYYMMDD") : "inicio"}_${t ? t.format("YYYYMMDD") : "hoy"}`
      : "todo";
    return { subtitle, rangeKey, onError: () => setSnack("No fue posible descargar el gráfico.") };
  }, [stats]);

  const totals = stats?.totals;
  const period = stats?.period;
  // Past two years the months are too thin to read: show years instead.
  const byYear = (stats?.trend || []).length > 24;
  const trend = (byYear ? stats?.yearTrend : stats?.trend) || [];
  const byDependence = stats?.breakdowns?.byDependence || [];
  // "Sin rol" / "Sin área" (the directory records, mostly) would dwarf every
  // real bar: they are left out of the charts and counted in the title. A role
  // the app doesn't know (a typo stored on a profile) is flagged as such.
  const roleBars = (stats?.breakdowns?.byRole || [])
    .filter((r) => r.key)
    .map((r) => (ROLE_LABELS[r.key] ? r : { ...r, label: `${r.key} (no reconocido)` }));
  const noRole = (stats?.breakdowns?.byRole || []).find((r) => !r.key);
  const levelBars = (stats?.breakdowns?.byLevel || []).filter((r) => r.key);
  const noLevel = (stats?.breakdowns?.byLevel || []).find((r) => !r.key);
  const leftOut = (entry, what) =>
    entry ? ` · ${fmtInt(entry.active + entry.inactive)} ${what} no se muestran` : "";

  const statusPie = useMemo(() => {
    if (!totals) return [];
    return [
      { id: 0, value: totals.active, label: `Activos (${fmtInt(totals.active)})`, color: ACTIVE },
      { id: 1, value: totals.inactive, label: `Inactivos (${fmtInt(totals.inactive)})`, color: INACTIVE },
    ].filter((d) => d.value > 0);
  }, [totals]);

  const StackedBars = ({ items }) => {
    const labels = items.map((d) => d.label);
    return (
      <BarChart
        layout="horizontal"
        height={Math.max(200, items.length * 34 + 70)}
        yAxis={[{ scaleType: "band", data: labels, valueFormatter: shortLabel }]}
        series={[
          { data: items.map((d) => d.active), label: "Activos", stack: "s", color: ACTIVE },
          { data: items.map((d) => d.inactive), label: "Inactivos", stack: "s", color: INACTIVE },
        ]}
        margin={{ left: barLabelMargin(labels), right: 24, top: 40, bottom: 30 }}
        sx={axisSx}
      />
    );
  };

  const filters = () => ({
    ...scope,
    status,
    roleColumns: roleColumns.map((r) => r.key),
    changes: withChanges ? rangeOf(from, to) : null,
  });

  const failure = (err, what) =>
    setSnack(err === "FORBIDDEN" ? ERROR_TEXT.FORBIDDEN : `No fue posible generar ${what}.`);

  const runPreview = async () => {
    setBusy("preview");
    const { body, error: err } = await previewUserReport(filters());
    setBusy(null);
    if (err) return failure(err, "la vista previa");
    setPreview(body);
    return body;
  };

  // A finding clicked on the dashboard: open the builder on that case's users.
  // Both follow the same filters and exclusions, so the counts match (findings
  // ignore the estado).
  const openFinding = async (key) => {
    setTab("builder");
    setFindingFilter(key);
    const { body, error: err } = await previewUserReport(filters());
    if (err) return failure(err, "la vista previa");
    scrollToFindings.current = true;
    setPreview(body);
  };

  useEffect(() => {
    if (!scrollToFindings.current || !preview || !findingsRef.current) return;
    scrollToFindings.current = false;
    // After the frame that lays out the builder, and without animation: a
    // smooth scroll started while the tab switches gets cut short.
    const el = findingsRef.current;
    requestAnimationFrame(() => el.scrollIntoView({ block: "start" }));
  }, [preview]);

  // Clicking the selected finding again shows all of them.
  const toggleFinding = (key) => setFindingFilter((current) => (current === key ? null : key));

  const runXlsx = async () => {
    setBusy("xlsx");
    const err = await downloadUserReportXlsx(filters());
    setBusy(null);
    if (err) failure(err, "el archivo de Excel");
  };

  const runCsv = async () => {
    setBusy("csv");
    const { body, error: err } = preview ? { body: preview, error: null } : await previewUserReport(filters());
    setBusy(null);
    if (err) return failure(err, "el CSV");
    setPreview(body);
    const statusLabel = { active: "activos", inactive: "inactivos", all: "todos" }[status];
    downloadCsv(`usuarios_${statusLabel}_${moment().format("YYYYMMDD")}.csv`, body.columns, body.rows);
  };

  if (!canView) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6">Acceso denegado</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Volver
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Informes de Usuarios
        </Typography>
        <Button
          variant="text"
          startIcon={<RefreshIcon />}
          onClick={() => load(from, to)}
          disabled={loading || !!invalidRange}
        >
          Actualizar
        </Button>
      </Stack>

      {/* Period: drives the dashboard and the builder's changes sheet. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {PRESETS.map((p) => (
              <Chip
                key={p.key}
                label={p.label}
                color={preset === p.key ? "primary" : "default"}
                variant={preset === p.key ? "filled" : "outlined"}
                onClick={() => applyPreset(p)}
              />
            ))}
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <DatePicker
              label="Desde"
              value={from}
              maxDate={to && to.isValid() ? to : undefined}
              disableFuture
              slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
              onChange={(m) => {
                setFrom(m);
                applyCustom(m, to);
              }}
            />
            <DatePicker
              label="Hasta"
              value={to}
              minDate={from && from.isValid() ? from : undefined}
              disableFuture
              slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
              onChange={(m) => {
                setTo(m);
                applyCustom(from, m);
              }}
            />
          </Stack>
        </Stack>
        {invalidRange && (
          <Typography variant="caption" color="error" component="p" sx={{ mt: 1, textAlign: "right" }}>
            {invalidRange}
          </Typography>
        )}
      </Paper>

      <SavedUserReports
        savedReports={savedReports}
        current={currentSaved}
        dirty={savedDirty}
        disabled={!options}
        onApply={applySaved}
        onSave={saveCurrent}
        onDelete={deleteCurrent}
      />

      {/* Filters and exclusions: drive the dashboard and the builder alike. */}
      <Accordion
        disableGutters
        variant="outlined"
        expanded={filtersOpen}
        onChange={(_, open) => setFiltersOpen(open)}
        sx={{ mb: 2, borderRadius: 2, "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flexGrow: 1, mr: 1 }}>
            <FilterListIcon fontSize="small" color={scopeChips.length ? "primary" : "action"} />
            <Typography variant="subtitle2" sx={{ mr: 1 }}>
              Filtros y exclusiones
            </Typography>
            {scopeChips.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Todos los usuarios
              </Typography>
            ) : (
              scopeChips.map((c) => <Chip key={c} size="small" color="primary" variant="outlined" label={c} />)
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
            Se aplican al informe visual (cifras, gráficos y hallazgos) y al generador de informes.
          </Typography>
          <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Autocomplete
                  multiple
                  size="small"
                  options={options?.dependencies || []}
                  value={dependencies}
                  onChange={(_, v) => setDependencies(v)}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.key === b.key}
                  renderOption={(props, o) => (
                    <li {...props} key={o.key}>
                      {o.label} ({fmtInt(o.count)})
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Dependencias" placeholder={dependencies.length ? "" : "Todas"} />
                  )}
                  noOptionsText={options ? "Sin coincidencias" : "Cargando dependencias…"}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Autocomplete
                  multiple
                  size="small"
                  options={ROLE_OPTIONS}
                  groupBy={(o) => o.group}
                  value={roles}
                  onChange={(_, v) => setRoles(v)}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.key === b.key}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filtrar por roles"
                      placeholder={roles.length ? "" : "Todos"}
                      helperText={
                        roles.length < 2
                          ? "Elija varios roles para listar a quienes tienen uno de ellos o todos a la vez."
                          : roleMatch === "all"
                          ? `Solo usuarios que tienen los ${roles.length} roles a la vez.`
                          : `Usuarios que tienen al menos uno de los ${roles.length} roles.`
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={roleMatch}
                  onChange={(_, v) => v && setRoleMatch(v)}
                  disabled={roles.length < 2}
                >
                  <ToggleButton value="any">Tiene alguno</ToggleButton>
                  <ToggleButton value="all">Tiene todos</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={accountOnly} onChange={(e) => setAccountOnly(e.target.checked)} />}
                  label="Solo usuarios con cuenta de acceso"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <UserReportExclusions
                  value={exclusions}
                  onChange={setExclusions}
                  options={options}
                  roleOptions={ROLE_OPTIONS}
                  appliesTo="del informe visual, el listado, la hoja de cambios y los hallazgos"
                />
              </Grid>
          </Grid>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button size="small" onClick={clearScope} disabled={scopeChips.length === 0}>
              Quitar filtros y exclusiones
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="dashboard" label="Informe visual" />
        <Tab value="builder" label="Generador de informes" />
      </Tabs>

      {tab === "dashboard" && (
        <>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
              <Typography color="error" gutterBottom>
                {ERROR_TEXT[error]}
              </Typography>
              {error !== "FORBIDDEN" && (
                <Button variant="contained" onClick={() => load(from, to)} disabled={!!invalidRange}>
                  Reintentar
                </Button>
              )}
            </Paper>
          )}

          {!loading && !error && stats && (
            <>
              {stats.narrowed && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Informe filtrado: las cifras, los gráficos y los hallazgos solo cuentan a{" "}
                  {plural(stats.totals.total, "usuario", "usuarios")}
                  {stats.excluded > 0 && ` (${plural(stats.excluded, "excluido", "excluidos")})`}. Ver «Filtros y
                  exclusiones».
                </Alert>
              )}
              <Typography variant="overline" color="text.secondary">
                Estado actual
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <StatCard
                    title="Usuarios registrados"
                    value={fmtInt(totals.total)}
                    subtitle={`${fmtInt(totals.staff)} funcionarios · ${fmtInt(totals.entity)} empresas`}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    title="Activos"
                    value={fmtInt(totals.active)}
                    subtitle={`${pctOf(totals.active, totals.total)} del total`}
                    color={ACTIVE}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    title="Inactivos"
                    value={fmtInt(totals.inactive)}
                    subtitle={`${pctOf(totals.inactive, totals.total)} del total · deshabilitados o con la cuenta de acceso bloqueada`}
                    color={totals.inactive > 0 ? INACTIVE : "text.primary"}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    title="Sin cuenta de acceso"
                    value={fmtInt(totals.withoutAccount)}
                    subtitle={`registros que no pueden ingresar · ${fmtInt(totals.neverSignedIn)} activos con cuenta nunca han ingresado`}
                  />
                </Grid>
              </Grid>

              <Typography variant="overline" color="text.secondary">
                En el período · {statsPeriodLabel}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6} md={3}>
                  <StatCard title="Creados" value={fmtInt(period.created)} subtitle="cuentas creadas" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard title="Actualizados" value={fmtInt(period.updated)} subtitle="usuarios con al menos un cambio de perfil, roles o correo" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    title="Cambios de perfil"
                    value={fmtInt(period.profileChanges)}
                    subtitle={`eventos, incluye cambios de correo · ${fmtInt(period.supportChanges)} de soporte (${pctOf(period.supportChanges, period.profileChanges)})`}
                    color={SUPPORT}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    title="Deshabilitados"
                    value={fmtInt(period.disabled)}
                    subtitle={`usuarios · ${fmtInt(period.enabled)} habilitados de nuevo`}
                    color={period.disabled > 0 ? INACTIVE : "text.primary"}
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
                {stats.auditSince
                  ? `Las actualizaciones y deshabilitaciones se registran desde el ${inBogota(stats.auditSince).format("DD/MM/YYYY")}; antes de esa fecha no hay historial.`
                  : "Aún no hay historial de cambios registrado."}
                {" "}Las creaciones se toman de la fecha de la cuenta de acceso. Un cambio es de soporte cuando lo hizo
                una cuenta con rol Administrador (según sus roles actuales).
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={8}>
                  <ChartCard
                    title={byYear ? "Movimiento de usuarios por año" : "Movimiento de usuarios por mes"}
                    empty={trend.every((t) => !t.created && !t.updated && !t.disabled)}
                    exportInfo={exportInfo}
                  >
                    <BarChart
                      height={300}
                      xAxis={[{ scaleType: "band", data: trend.map((t) => t.label) }]}
                      series={[
                        { data: trend.map((t) => t.created), label: "Creados", color: SERIES[3] },
                        { data: trend.map((t) => t.updated), label: "Actualizados", color: SERIES[1] },
                        { data: trend.map((t) => t.disabled), label: "Deshabilitados", color: INACTIVE },
                      ]}
                      sx={axisSx}
                    />
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ChartCard title="Activos e inactivos" empty={statusPie.length === 0} exportInfo={exportInfo}>
                    <PieChart
                      height={300}
                      series={[{ data: statusPie, innerRadius: 55, paddingAngle: 2, cornerRadius: 4 }]}
                      margin={{ top: 10, bottom: 60, left: 10, right: 10 }}
                      slotProps={{
                        legend: {
                          direction: "row",
                          position: { vertical: "bottom", horizontal: "middle" },
                          padding: 0,
                        },
                      }}
                    />
                  </ChartCard>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <ChartCard
                    title={byYear ? "Cambios de perfil por año" : "Cambios de perfil por mes"}
                    empty={trend.every((t) => !t.profileChanges)}
                    exportInfo={exportInfo}
                  >
                    <BarChart
                      height={280}
                      xAxis={[{ scaleType: "band", data: trend.map((t) => t.label) }]}
                      series={[
                        { data: trend.map((t) => t.supportChanges), label: "Soporte (Administrador)", stack: "p", color: SUPPORT },
                        {
                          data: trend.map((t) => t.profileChanges - t.supportChanges),
                          label: "Otros (el propio usuario u otros gestores)",
                          stack: "p",
                          color: SERIES[1],
                        },
                      ]}
                      sx={axisSx}
                    />
                  </ChartCard>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <ChartCard
                    title={
                      byDependence.length > TOP_DEPENDENCIES
                        ? `Por dependencia (las ${TOP_DEPENDENCIES} con más usuarios)`
                        : "Por dependencia"
                    }
                    empty={byDependence.length === 0}
                    exportInfo={exportInfo}
                  >
                    <StackedBars items={byDependence.slice(0, TOP_DEPENDENCIES)} />
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={7}>
                  <ChartCard title={`Por rol${leftOut(noRole, "sin rol")}`} empty={roleBars.length === 0} exportInfo={exportInfo}>
                    <StackedBars items={roleBars} />
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={5}>
                  <ChartCard
                    title={`Por área (nivel)${levelBars.length > TOP_DEPENDENCIES ? `, las ${TOP_DEPENDENCIES} más comunes` : ""}${leftOut(noLevel, "sin área")}`}
                    empty={levelBars.length === 0}
                    exportInfo={exportInfo}
                  >
                    <StackedBars items={levelBars.slice(0, TOP_DEPENDENCIES)} />
                  </ChartCard>
                </Grid>
              </Grid>

              {stats.findings?.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Hallazgos para revisión:{" "}
                    {fmtInt(stats.findings.reduce((n, f) => n + f.count, 0))}{" "}
                    {stats.narrowed ? "casos en los usuarios filtrados" : "casos en todo el sistema"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
                    {FINDINGS_NOTE} El listado por usuario está en la vista previa del Generador de informes.
                  </Typography>
                  <FindingsSummary summary={stats.findings} onSelect={openFinding} />
                </Paper>
              )}
            </>
          )}
        </>
      )}

      {tab === "builder" && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Estado
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={status}
                  onChange={(_, v) => v && setStatus(v)}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <ToggleButton key={o.key} value={o.key}>
                      {o.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} md={8}>
                <Autocomplete
                  multiple
                  size="small"
                  options={ROLE_OPTIONS}
                  groupBy={(o) => o.group}
                  value={roleColumns}
                  onChange={(_, v) => setRoleColumns(v)}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.key === b.key}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Roles a mostrar como columnas"
                      placeholder={roleColumns.length ? "" : "Todos los que tengan los usuarios listados"}
                      helperText="Una columna por rol, marcada con X para quien lo tiene. No filtra usuarios."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button size="small" onClick={() => setRoleColumns(INSTITUTIONAL_OPTIONS)}>
                    Columnas del listado institucional
                  </Button>
                  <Button size="small" onClick={() => setRoleColumns(roles)} disabled={roles.length === 0}>
                    Usar los roles de los filtros
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={withChanges} onChange={(e) => setWithChanges(e.target.checked)} />}
                  label={`Incluir hoja de cambios del período (${periodLabel}) de estos usuarios`}
                />
              </Grid>
            </Grid>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={busy === "preview" ? <CircularProgress size={18} /> : <TableViewIcon />}
                onClick={runPreview}
                disabled={!!busy || (withChanges && !!invalidRange)}
              >
                Vista previa
              </Button>
              <Button
                variant="contained"
                startIcon={busy === "xlsx" ? <CircularProgress size={18} color="inherit" /> : <GridOnIcon />}
                onClick={runXlsx}
                disabled={!!busy || (withChanges && !!invalidRange)}
              >
                Descargar Excel
              </Button>
              <Button
                variant="text"
                startIcon={busy === "csv" ? <CircularProgress size={18} /> : <DescriptionIcon />}
                onClick={runCsv}
                disabled={!!busy || (withChanges && !!invalidRange)}
              >
                Descargar CSV
              </Button>
            </Stack>
            {withChanges && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                El CSV solo incluye la lista de usuarios; la hoja de cambios va en el Excel.
              </Typography>
            )}
          </Paper>

          {preview && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {fmtInt(preview.total)} {preview.total === 1 ? "usuario" : "usuarios"}
                {preview.excluded > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    · {fmtInt(preview.excluded)} {preview.excluded === 1 ? "usuario excluido" : "usuarios excluidos"} del total del sistema
                  </Typography>
                )}
              </Typography>
              {preview.total === 0 ? (
                <Alert severity="info">Ningún usuario cumple los filtros.</Alert>
              ) : (
                <PreviewTable columns={preview.columns} rows={preview.rows} markMissing />
              )}
            </Paper>
          )}

          {preview?.changes && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {preview.changes.name}: {fmtInt(preview.changes.rows.length)}{" "}
                {preview.changes.rows.length === 1 ? "registro" : "registros"}
              </Typography>
              {preview.changes.rows.length === 0 ? (
                <Alert severity="info">No hay cambios registrados en el período.</Alert>
              ) : (
                <PreviewTable columns={preview.changes.columns} rows={preview.changes.rows} />
              )}
            </Paper>
          )}

          {preview?.findings && (
            <Paper ref={findingsRef} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, scrollMarginTop: 16 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Hallazgos para revisión: {fmtInt(preview.findings.rows.length)}{" "}
                {preview.findings.rows.length === 1 ? "caso" : "casos"}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
                {FINDINGS_NOTE} Siguen los filtros y las exclusiones (no el estado). No van en el Excel.
              </Typography>
              {preview.findings.rows.length === 0 ? (
                <Alert severity="success">Sin inconsistencias en los usuarios de este informe.</Alert>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
                    Haga clic en un hallazgo para ver solo sus casos.
                  </Typography>
                  <FindingsSummary summary={preview.findings.summary} selected={findingFilter} onSelect={toggleFinding} />
                  <Box sx={{ mt: 2 }}>
                    {selectedFinding && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Chip
                          color="warning"
                          label={`${selectedFinding.label}: ${fmtInt(findingRows.length)}`}
                          onDelete={() => setFindingFilter(null)}
                        />
                        <Button size="small" onClick={() => setFindingFilter(null)}>
                          Ver todos
                        </Button>
                      </Stack>
                    )}
                    <PreviewTable columns={preview.findings.columns} rows={findingRows} markMissing />
                  </Box>
                </>
              )}
            </Paper>
          )}
        </>
      )}

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack("")} message={snack} />
    </Container>
  );
}
